import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  CalendarOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button } from "antd";

import DatePicker from "../../../../Common/DatePicker/DatePicker";
import { MULTI_LOCATION_OPTIONS } from "../../constants";

import "./LocationToolbar.css";

export default function LocationToolbar({
  periodLabel = "May 15 - Jun 14, 2026",
  locations = MULTI_LOCATION_OPTIONS,
  selectedLocationKey = "all",
  onSelectLocation,
  onPeriodChange,
}) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [dateSelected, setDateSelected] = useState(periodLabel);
  const [prevDateSelected, setPrevDateSelected] = useState(periodLabel);

  useEffect(() => {
    if (periodLabel) {
      setDateSelected(periodLabel);
      setPrevDateSelected(periodLabel);
    }
  }, [periodLabel]);

  const { allLocation, siteLocations } = useMemo(() => {
    const all = locations.find((loc) => loc.key === "all")
      || { key: "all", label: "All locations" };
    return {
      allLocation: all,
      siteLocations: locations.filter((loc) => loc.key !== "all"),
    };
  }, [locations]);

  const updateScrollState = () => {
    const node = scrollerRef.current;
    if (!node) return;

    const { scrollLeft, scrollWidth, clientWidth } = node;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const node = scrollerRef.current;
    if (!node) return undefined;

    const handleResize = () => updateScrollState();
    node.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", handleResize);

    return () => {
      node.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", handleResize);
    };
  }, [siteLocations]);

  const scrollByAmount = (direction) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.max(180, node.clientWidth * 0.45), behavior: "smooth" });
  };

  const handleDateChange = (startDate, endDate) => {
    if (onPeriodChange) {
      onPeriodChange(startDate || "", endDate || "");
    }
  };

  const renderTab = (loc) => {
    const isActive = loc.key === selectedLocationKey;
    return (
      <Button
        key={loc.key}
        type="default"
        className={[
          "LocationToolbar__Tab",
          isActive ? "LocationToolbar__Tab--active" : "",
        ].filter(Boolean).join(" ")}
        onClick={() => {
          if (onSelectLocation) {
            onSelectLocation(loc.key);
          }
        }}
      >
        {loc.label}
      </Button>
    );
  };

  return (
    <div className="LocationToolbar">
      <DatePicker
        handleSearchDate={handleDateChange}
        value={dateSelected}
        onChange={setDateSelected}
        prevValue={prevDateSelected}
        setPrevValue={setPrevDateSelected}
        searchOnClear
        popoverPlacement="bottomLeft"
        allowFutureDateSelect
      >
        <Button
          type="default"
          className={`LocationToolbar__Period${dateSelected ? " LocationToolbar__Period--active" : ""}`}
          icon={<CalendarOutlined className="LocationToolbar__PeriodIcon" />}
        >
          <span className="LocationToolbar__PeriodLabel">
            {dateSelected || periodLabel}
          </span>
          <DownOutlined className="LocationToolbar__PeriodChevron" />
        </Button>
      </DatePicker>

      <div className="LocationToolbar__Locations">
        <Button
          type="text"
          className="LocationToolbar__ScrollButton"
          icon={<LeftOutlined />}
          disabled={!canScrollLeft}
          aria-label="Scroll locations left"
          onClick={() => scrollByAmount(-1)}
        />

        {renderTab(allLocation)}

        <span className="LocationToolbar__Divider" aria-hidden />

        <div className="LocationToolbar__Tabs" ref={scrollerRef}>
          {siteLocations.map(renderTab)}
        </div>

        <Button
          type="text"
          className="LocationToolbar__ScrollButton"
          icon={<RightOutlined />}
          disabled={!canScrollRight}
          aria-label="Scroll locations right"
          onClick={() => scrollByAmount(1)}
        />
      </div>
    </div>
  );
}
